import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  CircularProgress, TextField, IconButton, Tooltip,
  Divider, Alert, Button, Slider, Switch,
  FormControlLabel,
} from '@mui/material';
import {
  MyLocation, Refresh, ZoomIn, ZoomOut,
  NightsStay, WbSunny, Info, Star,
  CenterFocusStrong, CalendarMonth,
} from '@mui/icons-material';
import { skyAPI } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const apodImageSrc = (url) => {
  if (url && /^https?:\/\/apod\.nasa\.gov\//i.test(url)) {
    return `${API_BASE}/moon/apod-image?url=${encodeURIComponent(url)}`;
  }
  return url;
};

// ─── Star catalogue (RA/Dec in degrees, magnitude, color) ───────────────────
const STARS = [
  { id:'SIRIUS',     name:'Sirius',     ra:101.29, dec:-16.72, mag:-1.46, color:'#b0d0ff', size:5 },
  { id:'CANOPUS',    name:'Canopus',    ra: 95.99, dec:-52.70, mag:-0.74, color:'#fff8e7', size:4 },
  { id:'RIGIL_KENT', name:'α Centauri', ra:219.90, dec:-60.83, mag:-0.27, color:'#ffeecc', size:4 },
  { id:'ARCTURUS',   name:'Arcturus',   ra:213.92, dec: 19.18, mag:-0.05, color:'#ffaa55', size:4 },
  { id:'VEGA',       name:'Vega',       ra:279.23, dec: 38.78, mag: 0.03, color:'#d0e8ff', size:4 },
  { id:'CAPELLA',    name:'Capella',    ra: 79.17, dec: 45.99, mag: 0.08, color:'#fff0bb', size:4 },
  { id:'RIGEL',      name:'Rigel',      ra: 78.63, dec: -8.20, mag: 0.13, color:'#c0d8ff', size:4 },
  { id:'PROCYON',    name:'Procyon',    ra:114.83, dec:  5.23, mag: 0.34, color:'#fffaee', size:3 },
  { id:'BETELGEUSE', name:'Betelgeuse', ra: 88.79, dec:  7.41, mag: 0.50, color:'#ff7733', size:4 },
  { id:'ACHERNAR',   name:'Achernar',   ra: 24.43, dec:-57.24, mag: 0.46, color:'#aacfff', size:3 },
  { id:'HADAR',      name:'Hadar',      ra:210.96, dec:-60.37, mag: 0.60, color:'#b8d0ff', size:3 },
  { id:'ALTAIR',     name:'Altair',     ra:297.70, dec:  8.87, mag: 0.77, color:'#e8f8ff', size:3 },
  { id:'ALDEBARAN',  name:'Aldebaran',  ra: 68.98, dec: 16.51, mag: 0.85, color:'#ff8844', size:3 },
  { id:'ANTARES',    name:'Antares',    ra:247.35, dec:-26.43, mag: 1.06, color:'#ff4422', size:3 },
  { id:'SPICA',      name:'Spica',      ra:201.30, dec:-11.16, mag: 0.97, color:'#bbccff', size:3 },
  { id:'POLLUX',     name:'Pollux',     ra:116.33, dec: 28.03, mag: 1.14, color:'#ffbb77', size:3 },
  { id:'FOMALHAUT',  name:'Fomalhaut',  ra:344.41, dec:-29.62, mag: 1.16, color:'#eef8ff', size:3 },
  { id:'DENEB',      name:'Deneb',      ra:310.36, dec: 45.28, mag: 1.25, color:'#ddeeff', size:3 },
  { id:'POLARIS',    name:'Polaris',    ra:  2.53, dec: 89.26, mag: 1.97, color:'#eef6ff', size:3 },
  { id:'REGULUS',    name:'Regulus',    ra:152.09, dec: 11.97, mag: 1.40, color:'#c8e0ff', size:3 },
  { id:'CASTOR',     name:'Castor',     ra:113.65, dec: 31.89, mag: 1.58, color:'#ffffff', size:2 },
  { id:'MIMOSA',     name:'Mimosa',     ra:191.93, dec:-59.69, mag: 1.25, color:'#aad0ff', size:3 },
  { id:'BELLATRIX',  name:'Bellatrix',  ra: 81.28, dec:  6.35, mag: 1.64, color:'#aac8ff', size:2 },
  { id:'ELNATH',     name:'Elnath',     ra: 81.57, dec: 28.61, mag: 1.65, color:'#ccddff', size:2 },
  { id:'ALNILAM',    name:'Alnilam',    ra: 84.05, dec: -1.20, mag: 1.69, color:'#bbccff', size:2 },
  { id:'ALNITAK',    name:'Alnitak',    ra: 85.19, dec: -1.94, mag: 1.77, color:'#aabbff', size:2 },
  { id:'MINTAKA',    name:'Mintaka',    ra: 83.00, dec: -0.30, mag: 2.25, color:'#bbccff', size:2 },
  { id:'ADHARA',     name:'Adhara',     ra:104.66, dec:-28.97, mag: 1.50, color:'#b0ccff', size:3 },
  { id:'SHAULA',     name:'Shaula',     ra:263.40, dec:-37.10, mag: 1.62, color:'#aabbff', size:2 },
  { id:'SARGAS',     name:'Sargas',     ra:264.33, dec:-43.00, mag: 1.86, color:'#ffeecc', size:2 },
  { id:'KAUS_AUS',   name:'Kaus Australis', ra:276.04, dec:-34.38, mag: 1.79, color:'#ffddaa', size:2 },
  // add more faint stars as background
  ...Array.from({length:180}, (_,i) => ({
    id:`BG${i}`, name:`HD${10000+i*317}`,
    ra: (i * 137.508) % 360,
    dec: ((i * 79.3) % 180) - 90,
    mag: 2.5 + Math.random()*2.5,
    color: ['#ffffff','#ffe8d0','#d0e8ff','#fff0cc'][i%4],
    size: 1,
  })),
];

// ─── Constellations (pairs of star RAs to draw lines) ───────────────────────
const CONSTELLATION_LINES = [
  // Orion
  { name:'Orion', stars:['BETELGEUSE','ALNILAM'], color:'rgba(100,150,255,0.35)' },
  { name:'Orion', stars:['ALNILAM','RIGEL'],      color:'rgba(100,150,255,0.35)' },
  { name:'Orion', stars:['ALNILAM','ALNITAK'],    color:'rgba(100,150,255,0.35)' },
  { name:'Orion', stars:['ALNITAK','MINTAKA'],    color:'rgba(100,150,255,0.35)' },
  { name:'Orion', stars:['BETELGEUSE','BELLATRIX'],color:'rgba(100,150,255,0.35)' },
  // Canis Major
  { name:'Canis Major', stars:['SIRIUS','ADHARA'],  color:'rgba(100,200,255,0.3)' },
  // Gemini
  { name:'Gemini', stars:['POLLUX','CASTOR'],       color:'rgba(200,150,255,0.3)' },
  // Taurus
  { name:'Taurus', stars:['ALDEBARAN','ELNATH'],    color:'rgba(255,150,100,0.3)' },
  // Scorpius
  { name:'Scorpius', stars:['ANTARES','SHAULA'],    color:'rgba(255,100,100,0.3)' },
  { name:'Scorpius', stars:['SHAULA','SARGAS'],     color:'rgba(255,100,100,0.3)' },
  // Sagittarius
  { name:'Sagittarius', stars:['KAUS_AUS','SHAULA'],color:'rgba(255,180,80,0.3)'  },
];

// ─── Asterisms ───────────────────────────────────────────────────────────────
const ASTERISMS = [
  {
    name: 'Big Dipper', color: 'rgba(255,220,100,0.5)',
    // approximate RA/Dec for the 7 stars
    points: [{ra:165.9,dec:61.8},{ra:183.9,dec:57.0},{ra:193.5,dec:55.9},
             {ra:200.9,dec:54.9},{ra:206.9,dec:49.3},{ra:210.0,dec:56.4},{ra:206.9,dec:49.3}],
  },
  {
    name: 'Summer Triangle', color: 'rgba(150,255,200,0.4)',
    points: [{ra:279.2,dec:38.8},{ra:297.7,dec:8.9},{ra:310.4,dec:45.3}],
    closed: true,
  },
  {
    name: 'Winter Triangle', color: 'rgba(200,220,255,0.4)',
    points: [{ra:101.3,dec:-16.7},{ra:114.8,dec:5.2},{ra:88.8,dec:7.4}],
    closed: true,
  },
  {
    name: "Orion's Belt", color: 'rgba(255,200,100,0.6)',
    points: [{ra:83.0,dec:-0.3},{ra:84.1,dec:-1.2},{ra:85.2,dec:-1.9}],
  },
];

// ─── Utility: RA/Dec → canvas XY (stereographic projection) ────────────────
function raDecToXY(ra, dec, lst, lat, W, H, zoom, cx, cy) {
  const raR  = (ra * Math.PI) / 180;
  const decR = (dec * Math.PI) / 180;
  const ha   = lst - raR;
  const latR = (lat * Math.PI) / 180;
  const alt  = Math.asin(
    Math.sin(latR) * Math.sin(decR) +
    Math.cos(latR) * Math.cos(decR) * Math.cos(ha)
  );
  const az = Math.atan2(
    -Math.cos(decR) * Math.sin(ha),
    Math.sin(decR) * Math.cos(latR) - Math.cos(decR) * Math.cos(ha) * Math.sin(latR)
  );
  if (alt < -0.05) return null; // below horizon

  const r = Math.cos(alt);
  const x = cx + (W * 0.44 * zoom) * r * Math.sin(az);
  const y = cy - (W * 0.44 * zoom) * r * Math.cos(az);
  return { x, y, alt: (alt * 180) / Math.PI, az: (az * 180) / Math.PI };
}

function getLST(jd, lon) {
  const T   = (jd - 2451545.0) / 36525.0;
  const gst = 280.46061837 + 360.98564736629 * (jd - 2451545.0) +
              0.000387933 * T * T;
  return ((gst + lon) % 360) * (Math.PI / 180);
}

function dateTimeToJD(dateStr, hour) {
  const d   = new Date(`${dateStr}T${String(hour).padStart(2,'0')}:00:00Z`);
  return d.getTime() / 86400000 + 2440587.5;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function NightSkyPage() {
  const canvasRef               = useRef(null);
  const animRef                 = useRef(null);
  const [location, setLocation] = useState(null);
  const [date, setDate]         = useState(new Date().toISOString().split('T')[0]);
  const [hour, setHour]         = useState(22);
  const [skyData, setSkyData]   = useState(null);
  const [apod, setApod]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [apodLoading, setApodLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom]         = useState(1.0);
  const [showLabels, setShowLabels]   = useState(true);
  const [showConst,  setShowConst]    = useState(true);
  const [showGrid,   setShowGrid]     = useState(false);
  const [isDayMode,  setIsDayMode]    = useState(false);
  const [locError,   setLocError]     = useState('');

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        p => setLocation({ lat: p.coords.latitude, lon: p.coords.longitude }),
        () => { setLocError('Location denied — using Mumbai'); setLocation({ lat: 19.076, lon: 72.877 }); }
      );
    } else {
      setLocation({ lat: 19.076, lon: 72.877 });
    }
  }, []);

  // Fetch sky data
  const fetchSkyData = useCallback(async () => {
    if (!location) return;
    setLoading(true);
    try {
      const res = await skyAPI.getSkyData(location.lat, location.lon, date, hour);
      setSkyData(res.data);
      setIsDayMode(!res.data.isDaytime ? false : true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [location, date, hour]);

  // Fetch APOD
  const fetchAPOD = useCallback(async () => {
    setApodLoading(true);
    try {
      const res = await skyAPI.getAPOD(date);
      setApod(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setApodLoading(false);
    }
  }, [date]);

  useEffect(() => { if (location) { fetchSkyData(); fetchAPOD(); } }, [location, date, hour]);

  // ── Canvas drawing ──────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const W   = canvas.width  = canvas.offsetWidth;
      const H   = canvas.height = canvas.offsetHeight;
      const ctx = canvas.getContext('2d');
      const cx  = W / 2, cy = H / 2;

      const lat = location?.lat ?? 19;
      const lon = location?.lon ?? 73;
      const jd  = dateTimeToJD(date, hour);
      const lst = getLST(jd, lon);

      const day = isDayMode || (skyData && skyData.isDaytime);

      // ── Background ──────────────────────────────────────────────────────
      if (day) {
        const sky = ctx.createRadialGradient(cx, cy*0.4, 0, cx, cy, H*0.8);
        sky.addColorStop(0,   '#87CEEB');
        sky.addColorStop(0.6, '#5BA3D9');
        sky.addColorStop(1,   '#2E5F8A');
        ctx.fillStyle = sky;
      } else {
        const sky = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(W,H)*0.7);
        sky.addColorStop(0,   '#0a0e1a');
        sky.addColorStop(0.5, '#060910');
        sky.addColorStop(1,   '#020408');
        ctx.fillStyle = sky;
      }
      ctx.fillRect(0, 0, W, H);

      // ── Grid ────────────────────────────────────────────────────────────
      if (showGrid && !day) {
        ctx.strokeStyle = 'rgba(255,255,255,0.12)';
        ctx.lineWidth   = 0.5;
        for (let a = 0; a < 360; a += 30) {
          const r = Math.PI / 180;
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.lineTo(cx + W * 0.5 * zoom * Math.sin(a * r),
                     cy - W * 0.5 * zoom * Math.cos(a * r));
          ctx.stroke();
        }
        for (let alt = 10; alt < 90; alt += 20) {
          const r = (90 - alt) / 90 * W * 0.44 * zoom;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = 'rgba(255,255,255,0.3)';
          ctx.font      = '9px monospace';
          ctx.fillText(`${alt}°`, cx + r + 3, cy);
        }
      }

      // ── Horizon ─────────────────────────────────────────────────────────
      const horizonR = W * 0.44 * zoom;
      const hGrad    = ctx.createRadialGradient(cx, cy, horizonR*0.85, cx, cy, horizonR*1.1);
      hGrad.addColorStop(0, day ? 'rgba(34,80,34,0.6)' : 'rgba(10,30,10,0.8)');
      hGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.beginPath();
      ctx.arc(cx, cy, horizonR * 1.05, 0, Math.PI * 2);
      ctx.fillStyle = hGrad;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(cx, cy, horizonR, 0, Math.PI * 2);
      ctx.strokeStyle = day ? 'rgba(100,180,100,0.6)' : 'rgba(255,255,255,0.25)';
      ctx.lineWidth   = 1.5;
      ctx.stroke();

      // Compass labels
      const dirs = [['N',0],['E',90],['S',180],['W',270]];
      ctx.font      = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      dirs.forEach(([d, az]) => {
        const r   = horizonR + 18;
        const ang = Math.PI * az / 180;
        ctx.fillStyle = day ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.7)';
        ctx.fillText(d, cx + r * Math.sin(ang), cy - r * Math.cos(ang) + 5);
      });

      if (!day) {
        // ── Constellation lines ──────────────────────────────────────────
        if (showConst) {
          CONSTELLATION_LINES.forEach(cl => {
            const s1 = STARS.find(s => s.id === cl.stars[0]);
            const s2 = STARS.find(s => s.id === cl.stars[1]);
            if (!s1 || !s2) return;
            const p1 = raDecToXY(s1.ra, s1.dec, lst, lat, W, H, zoom, cx, cy);
            const p2 = raDecToXY(s2.ra, s2.dec, lst, lat, W, H, zoom, cx, cy);
            if (!p1 || !p2) return;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = cl.color;
            ctx.lineWidth   = 1.2;
            ctx.stroke();
          });
        }

        // ── Asterisms ────────────────────────────────────────────────────
        ASTERISMS.forEach(ast => {
          const pts = ast.points
            .map(p => raDecToXY(p.ra, p.dec, lst, lat, W, H, zoom, cx, cy))
            .filter(Boolean);
          if (pts.length < 2) return;
          ctx.beginPath();
          pts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
          if (ast.closed) ctx.closePath();
          ctx.strokeStyle = ast.color;
          ctx.lineWidth   = 1.8;
          ctx.stroke();
          // Asterism label
          if (showLabels && pts.length > 0) {
            const midX = pts.reduce((s, p) => s + p.x, 0) / pts.length;
            const midY = pts.reduce((s, p) => s + p.y, 0) / pts.length;
            ctx.fillStyle = ast.color;
            ctx.font      = 'bold 10px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(ast.name, midX, midY - 10);
          }
        });

        // ── Stars ────────────────────────────────────────────────────────
        STARS.forEach(star => {
          const pos = raDecToXY(star.ra, star.dec, lst, lat, W, H, zoom, cx, cy);
          if (!pos) return;

          const sz = Math.max(0.8, star.size - (star.mag / 4));
          const brightness = Math.max(0.4, 1 - star.mag / 8);

          // Twinkle for bright stars
          const tw = star.mag < 1.5
            ? 0.85 + 0.15 * Math.sin(Date.now() / (800 + star.ra * 10))
            : 1;

          if (star.size >= 3) {
            const glow = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, sz * 4);
            glow.addColorStop(0, star.color + 'aa');
            glow.addColorStop(1, 'transparent');
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, sz * 4, 0, Math.PI * 2);
            ctx.fillStyle = glow;
            ctx.fill();
          }

          ctx.beginPath();
          ctx.arc(pos.x, pos.y, sz * tw, 0, Math.PI * 2);
          ctx.fillStyle = star.color;
          ctx.globalAlpha = brightness;
          ctx.fill();
          ctx.globalAlpha = 1;

          if (showLabels && star.mag < 1.8 && star.size >= 3) {
            ctx.fillStyle = 'rgba(220,240,255,0.75)';
            ctx.font      = '10px sans-serif';
            ctx.textAlign = 'left';
            ctx.fillText(star.name, pos.x + sz + 4, pos.y - sz + 2);
          }

          star._px = pos.x; star._py = pos.y; star._sz = sz;
        });

        // ── Moon ────────────────────────────────────────────────────────
        const moonRa  = getMoonRA(jd);
        const moonDec = getMoonDec(jd);
        const moonPos = raDecToXY(moonRa, moonDec, lst, lat, W, H, zoom, cx, cy);
        if (moonPos) {
          const illum  = skyData?.moonPhase?.illumination ?? 50;
          const mGrad  = ctx.createRadialGradient(moonPos.x, moonPos.y, 0, moonPos.x, moonPos.y, 14 * zoom);
          mGrad.addColorStop(0, '#ffffff');
          mGrad.addColorStop(0.5, '#e8e8d8');
          mGrad.addColorStop(1, '#c8c8b8');
          ctx.beginPath();
          ctx.arc(moonPos.x, moonPos.y, 14 * zoom, 0, Math.PI * 2);
          ctx.fillStyle = mGrad;
          ctx.fill();

          // Moon shadow
          ctx.beginPath();
          ctx.arc(moonPos.x + (14 * zoom * (1 - illum/50) * (illum < 50 ? -1 : 1)),
                  moonPos.y, 14 * zoom, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(5,10,20,0.85)';
          ctx.fill();

          if (showLabels) {
            ctx.fillStyle = 'rgba(230,230,200,0.9)';
            ctx.font      = 'bold 11px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(`🌕 ${skyData?.moonPhase?.name ?? 'Moon'}`, moonPos.x, moonPos.y + 24 * zoom);
          }
          moonPos._id = 'MOON';
          moonPos._name = 'Moon';
          moonPos._r    = 14 * zoom;
        }

        // ── ISS ─────────────────────────────────────────────────────────
        const iss = skyData?.iss?.iss_position;
        if (iss) {
          const issLat = parseFloat(iss.latitude);
          const issLon = parseFloat(iss.longitude);
          // Convert ISS geographic coords to equatorial:
          // ISS RA = LST at ISS longitude (sub-satellite point)
          // ISS Dec ≈ geographic latitude (valid approximation for LEO)
          const lstDeg  = (lst * 180) / Math.PI;
          const issLstDeg = ((lstDeg - (lon - issLon)) % 360 + 360) % 360;
          const issRa   = issLstDeg;
          const issPos = raDecToXY(issRa, issLat, lst, lat, W, H, zoom, cx, cy);
          if (issPos) {
            ctx.beginPath();
            ctx.arc(issPos.x, issPos.y, 5 * zoom, 0, Math.PI * 2);
            ctx.fillStyle   = '#4fc3f7';
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth   = 1.5;
            ctx.stroke();
            if (showLabels) {
              ctx.fillStyle = '#4fc3f7';
              ctx.font      = 'bold 10px sans-serif';
              ctx.textAlign = 'left';
              ctx.fillText('🛸 ISS', issPos.x + 8, issPos.y + 4);
            }
          }
        }

        // ── Backend Planets ──────────────────────────────────────────────
        (skyData?.planets ?? []).forEach(planet => {
          const lon2  = planet.eclLon ?? 0;
          const pPos  = raDecToXY(lon2, 0, lst, lat, W, H, zoom, cx, cy);
          if (!pPos || !planet.visible) return;

          const sz = (planet.size ?? 5) * zoom * 0.6;
          const pg = ctx.createRadialGradient(pPos.x, pPos.y, 0, pPos.x, pPos.y, sz * 2);
          pg.addColorStop(0, planet.color + 'cc');
          pg.addColorStop(1, 'transparent');
          ctx.beginPath();
          ctx.arc(pPos.x, pPos.y, sz * 2, 0, Math.PI * 2);
          ctx.fillStyle = pg;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(pPos.x, pPos.y, sz, 0, Math.PI * 2);
          ctx.fillStyle = planet.color;
          ctx.fill();

          if (showLabels) {
            ctx.fillStyle = planet.color;
            ctx.font      = 'bold 11px sans-serif';
            ctx.textAlign = 'left';
            ctx.fillText(planet.name, pPos.x + sz + 5, pPos.y - sz + 3);
          }
          planet._px = pPos.x; planet._py = pPos.y; planet._sz = sz;
        });

      } else {
        // ── Day Mode: Sun ────────────────────────────────────────────────
        const sun    = skyData?.sunPosition;
        const sunAlt = sun?.altitude ?? 45;
        const sunAz  = sun?.azimuth  ?? 180;
        const sunAng = Math.PI * sunAz / 180;
        const sunR   = (90 - Math.max(0, sunAlt)) / 90 * horizonR;
        const sunX   = cx + sunR * Math.sin(sunAng);
        const sunY   = cy - sunR * Math.cos(sunAng);

        const sGlow  = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 80 * zoom);
        sGlow.addColorStop(0,   'rgba(255,255,200,0.9)');
        sGlow.addColorStop(0.3, 'rgba(255,220,80,0.6)');
        sGlow.addColorStop(1,   'transparent');
        ctx.beginPath();
        ctx.arc(sunX, sunY, 80 * zoom, 0, Math.PI * 2);
        ctx.fillStyle = sGlow;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(sunX, sunY, 22 * zoom, 0, Math.PI * 2);
        ctx.fillStyle = '#fff8c0';
        ctx.fill();

        if (showLabels) {
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.font      = 'bold 12px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('☀ Sun', sunX, sunY + 32 * zoom);
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [location, date, hour, zoom, showLabels, showConst, showGrid, isDayMode, skyData]);

  // ── Click detection ─────────────────────────────────────────────────────────
  const handleCanvasClick = useCallback(async (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx   = e.clientX - rect.left;
    const my   = e.clientY - rect.top;

    // Check stars
    for (const star of STARS) {
      if (!star._px) continue;
      const d = Math.hypot(mx - star._px, my - star._py);
      if (d < Math.max(8, (star._sz ?? 2) * 3)) {
        try {
          const res = await skyAPI.getObjectInfo(star.id, date);
          setSelected({ ...res.data, _type: 'star' });
        } catch {
          setSelected({ name: star.name, id: star.id, magnitude: star.mag, type: 'Star' });
        }
        return;
      }
    }

    // Check planets
    for (const p of (skyData?.planets ?? [])) {
      if (!p._px) continue;
      const d = Math.hypot(mx - p._px, my - p._py);
      if (d < 20) {
        try {
          const res = await skyAPI.getObjectInfo(p.name.toUpperCase(), date);
          setSelected({ ...res.data, altitude: p.altitude, azimuth: p.azimuth, distance: `${p.distance} AU` });
        } catch {
          setSelected({ name: p.name, type: 'Planet', altitude: p.altitude, azimuth: p.azimuth });
        }
        return;
      }
    }

    setSelected(null);
  }, [skyData, date]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function getMoonRA(jd) {
    const D = jd - 2451545.0;
    return ((218.316 + 13.176396 * D) % 360 + 360) % 360;
  }
  function getMoonDec(jd) {
    const D = jd - 2451545.0;
    return 5.128 * Math.sin((93.272 + 13.229350 * D) * Math.PI / 180);
  }

  const isDay = skyData?.isDaytime ?? false;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, flexWrap:'wrap', gap:1.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight:800 }}>
            🌌 Night Sky Viewer
          </Typography>
          <Typography variant="body2" sx={{ color:'text.secondary', mt:0.5 }}>
            Interactive sky map • Real planetary positions • NASA APOD
          </Typography>
        </Box>
        <Box sx={{ display:'flex', gap:1, alignItems:'center', flexWrap:'wrap' }}>
          <Chip
            label={isDay ? '☀ Day Mode' : '🌙 Night Mode'}
            sx={{
              bgcolor: isDay ? 'rgba(255,220,80,0.2)' : 'rgba(255,255,255,0.15)',
              color:   isDay ? '#ffd600' : 'primary.light',
              fontWeight:700,
            }}
          />
          {loading && <CircularProgress size={20} sx={{ color:'primary.main' }} />}
          <Button size="small" startIcon={<Refresh />} variant="outlined"
            onClick={fetchSkyData}
            sx={{ borderColor:'rgba(255,255,255,0.4)', color:'primary.light' }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {locError && (
        <Alert severity="info" sx={{ mb:2, bgcolor:'rgba(2,136,209,0.1)', border:'1px solid rgba(2,136,209,0.3)' }}>
          {locError}
        </Alert>
      )}

      {/* Controls */}
      <Card sx={{ mb:2, border:'1px solid rgba(255,255,255,0.15)' }}>
        <CardContent sx={{ p:2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth size="small" type="date" label="Select Date"
                value={date} onChange={e => setDate(e.target.value)}
                InputLabelProps={{ shrink:true }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Typography variant="caption" sx={{ color:'text.secondary' }}>
                Hour: {String(hour).padStart(2,'0')}:00
              </Typography>
              <Slider
                value={hour} min={0} max={23} step={1}
                onChange={(_, v) => setHour(v)}
                sx={{ color:'primary.main', mt:0.5 }}
                marks={[{value:0,label:'00'},{value:6,label:'06'},{value:12,label:'12'},{value:18,label:'18'},{value:23,label:'23'}]}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              {location && (
                <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                  <MyLocation sx={{ color:'primary.main', fontSize:16 }} />
                  <Typography variant="caption" sx={{ color:'text.secondary' }}>
                    {location.lat.toFixed(2)}°, {location.lon.toFixed(2)}°
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display:'flex', gap:0.5, flexWrap:'wrap' }}>
                <FormControlLabel
                  control={<Switch checked={showLabels} size="small" onChange={e=>setShowLabels(e.target.checked)} />}
                  label={<Typography variant="caption">Labels</Typography>}
                />
                <FormControlLabel
                  control={<Switch checked={showConst} size="small" onChange={e=>setShowConst(e.target.checked)} />}
                  label={<Typography variant="caption">Lines</Typography>}
                />
                <FormControlLabel
                  control={<Switch checked={showGrid} size="small" onChange={e=>setShowGrid(e.target.checked)} />}
                  label={<Typography variant="caption">Grid</Typography>}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Sky Canvas */}
        <Grid item xs={12} md={selected ? 7 : 9}>
          <Card sx={{ overflow:'hidden', border:'1px solid rgba(255,255,255,0.2)', position:'relative' }}>
            <canvas
              ref={canvasRef}
              onClick={handleCanvasClick}
              style={{ width:'100%', height:'580px', display:'block', cursor:'crosshair' }}
            />
            {/* Zoom controls */}
            <Box sx={{ position:'absolute', top:12, right:12, display:'flex', flexDirection:'column', gap:0.5 }}>
              {[
                { icon:<ZoomIn />,            tip:'Zoom In',    fn:()=>setZoom(z=>Math.min(z+0.2,2.5)) },
                { icon:<ZoomOut />,           tip:'Zoom Out',   fn:()=>setZoom(z=>Math.max(z-0.2,0.4)) },
                { icon:<CenterFocusStrong />, tip:'Reset Zoom', fn:()=>setZoom(1.0) },
              ].map(({ icon, tip, fn }) => (
                <Tooltip key={tip} title={tip} placement="left">
                  <IconButton onClick={fn} size="small"
                    sx={{ bgcolor:'rgba(10,22,40,0.8)', color:'primary.light', border:'1px solid rgba(255,255,255,0.3)',
                          '&:hover':{ bgcolor:'rgba(2,136,209,0.3)' } }}>
                    {icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
            <Box sx={{ px:2, py:1, display:'flex', gap:2, flexWrap:'wrap', bgcolor:'rgba(5,13,26,0.7)' }}>
              {[
                { color:'#ffffff', label:'Stars' },
                { color:'rgba(100,150,255,0.6)', label:'Constellations' },
                { color:'rgba(255,220,100,0.7)', label:'Asterisms' },
                { color:'#4fc3f7', label:'Planets' },
                { color:'#e8e8d8', label:'Moon' },
                { color:'#4fc3f7', label:'ISS' },
              ].map(({ color, label }) => (
                <Box key={label} sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                  <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:color }} />
                  <Typography variant="caption" sx={{ color:'text.secondary', fontSize:10 }}>{label}</Typography>
                </Box>
              ))}
              <Typography variant="caption" sx={{ color:'text.secondary', ml:'auto', fontSize:10 }}>
                Click any object for info
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Side panels */}
        <Grid item xs={12} md={selected ? 5 : 3}>
          <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>

            {/* Moon & Sky Info */}
            <Card sx={{ border:'1px solid rgba(255,255,255,0.15)' }}>
              <CardContent sx={{ p:2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight:700, color:'primary.light', mb:1.5 }}>
                  🌙 Tonight's Sky
                </Typography>
                {skyData ? (
                  <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
                    <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                      <Typography variant="caption" sx={{ color:'text.secondary' }}>Mode</Typography>
                      <Typography variant="caption" sx={{ color: isDay ? '#ffd600' : 'primary.light', fontWeight:700 }}>
                        {isDay ? '☀ Daytime' : '🌙 Night'}
                      </Typography>
                    </Box>
                    <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                      <Typography variant="caption" sx={{ color:'text.secondary' }}>Moon Phase</Typography>
                      <Typography variant="caption" sx={{ color:'text.primary', fontWeight:700 }}>
                        {skyData.moonPhase?.emoji} {skyData.moonPhase?.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                      <Typography variant="caption" sx={{ color:'text.secondary' }}>Illumination</Typography>
                      <Typography variant="caption" sx={{ color:'text.primary', fontWeight:700 }}>
                        {skyData.moonPhase?.illumination}%
                      </Typography>
                    </Box>
                    <Divider sx={{ borderColor:'rgba(255,255,255,0.08)' }} />
                    <Typography variant="caption" sx={{ color:'text.secondary', fontWeight:700, mt:0.5 }}>
                      Visible Planets
                    </Typography>
                    {skyData.planets?.filter(p => p.visible).map(p => (
                      <Box key={p.name} sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <Box sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
                          <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:p.color }} />
                          <Typography variant="caption" sx={{ color:'text.primary' }}>{p.name}</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color:'text.secondary' }}>
                          alt {p.altitude?.toFixed(1)}°
                        </Typography>
                      </Box>
                    ))}
                    {skyData.planets?.filter(p=>p.visible).length === 0 && (
                      <Typography variant="caption" sx={{ color:'text.secondary' }}>No planets visible</Typography>
                    )}
                  </Box>
                ) : (
                  <CircularProgress size={20} sx={{ color:'primary.main' }} />
                )}
              </CardContent>
            </Card>

            {/* Selected Object Info */}
            {selected && (
              <Card sx={{ border:`1px solid rgba(255,255,255,0.3)`, bgcolor:'rgba(2,136,209,0.05)' }}>
                <CardContent sx={{ p:2 }}>
                  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight:800, color:'primary.light' }}>
                      <Info sx={{ fontSize:16, mr:0.5, verticalAlign:'middle' }} />
                      {selected.name}
                    </Typography>
                    <Chip label={selected.type} size="small"
                      sx={{ bgcolor:'rgba(255,255,255,0.15)', color:'primary.light', fontSize:10, fontWeight:700 }} />
                  </Box>
                  {selected.description && (
                    <Typography variant="caption" sx={{ color:'text.secondary', lineHeight:1.7, display:'block', mb:1.5 }}>
                      {selected.description}
                    </Typography>
                  )}
                  <Divider sx={{ borderColor:'rgba(255,255,255,0.08)', mb:1 }} />
                  {[
                    ['Magnitude',    selected.magnitude],
                    ['Distance',     selected.distance],
                    ['Constellation',selected.constellation],
                    ['Altitude',     selected.altitude ? `${Number(selected.altitude).toFixed(1)}°` : null],
                    ['Azimuth',      selected.azimuth  ? `${Number(selected.azimuth).toFixed(1)}°`  : null],
                  ].map(([label, val]) => val != null && (
                    <Box key={label} sx={{ display:'flex', justifyContent:'space-between', py:0.5,
                      borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
                      <Typography variant="caption" sx={{ color:'text.secondary' }}>{label}</Typography>
                      <Typography variant="caption" sx={{ color:'text.primary', fontWeight:700 }}>{val}</Typography>
                    </Box>
                  ))}
                  <Button size="small" onClick={()=>setSelected(null)} sx={{ mt:1.5, color:'text.secondary' }}>
                    Close
                  </Button>
                </CardContent>
              </Card>
            )}
          </Box>
        </Grid>

        {/* APOD Panel */}
        <Grid item xs={12}>
          <Card sx={{ border:'1px solid rgba(255,255,255,0.15)' }}>
            <CardContent sx={{ p:2.5 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:2 }}>
                <CalendarMonth sx={{ color:'primary.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight:700 }}>
                  NASA Astronomy Picture of the Day
                </Typography>
                <Chip label={date} size="small"
                  sx={{ bgcolor:'rgba(255,255,255,0.1)', color:'primary.light', fontSize:11 }} />
              </Box>

              {apodLoading ? (
                <Box sx={{ display:'flex', gap:2, alignItems:'center' }}>
                  <CircularProgress size={24} sx={{ color:'primary.main' }} />
                  <Typography variant="body2" sx={{ color:'text.secondary' }}>Loading APOD...</Typography>
                </Box>
              ) : apod ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    {apod.url && apod.media_type === 'image' ? (
                      <Box
                        component="img" src={apodImageSrc(apod.url)} alt={apod.title}
                        sx={{ width:'100%', borderRadius:2, maxHeight:220, objectFit:'cover',
                              border:'1px solid rgba(255,255,255,0.2)' }}
                      />
                    ) : apod.media_type === 'video' ? (
                      <Box component="iframe" src={apod.url}
                        sx={{ width:'100%', height:220, borderRadius:2,
                              border:'1px solid rgba(255,255,255,0.2)' }} />
                    ) : (
                      <Box sx={{ height:220, borderRadius:2, bgcolor:'rgba(255,255,255,0.05)',
                        border:'1px solid rgba(255,255,255,0.1)', display:'flex',
                        alignItems:'center', justifyContent:'center' }}>
                        <Typography sx={{ fontSize:40 }}>🔭</Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" sx={{ fontWeight:800, color:'primary.light', mb:1 }}>
                      {apod.title}
                    </Typography>
                    {apod.copyright && (
                      <Typography variant="caption" sx={{ color:'text.secondary', display:'block', mb:1 }}>
                        © {apod.copyright}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color:'text.secondary', lineHeight:1.8 }}>
                      {apod.explanation?.slice(0, 400)}{apod.explanation?.length > 400 ? '...' : ''}
                    </Typography>
                    {apod.url && (
                      <Button size="small" variant="outlined"
                        href={apod.hdurl || apod.url} target="_blank" rel="noopener noreferrer"
                        sx={{ mt:1.5, borderColor:'rgba(255,255,255,0.4)', color:'primary.light' }}>
                        View Full Image ↗
                      </Button>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" sx={{ color:'text.secondary' }}>
                  APOD unavailable for this date.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}